import type { FacilityReportBranding, LetterheadLayout, SignatureLayout } from '../../../types/facility.type'
import { DEFAULT_LETTERHEAD_LAYOUT, DEFAULT_SIGNATURE_LAYOUT } from './resolveReportLayouts'

export const LETTERHEAD_LAYOUT_OPTIONS: { label: string; value: LetterheadLayout; hint: string }[] = [
  { label: 'Logo left', value: 'logo_left', hint: 'Logo | Letterhead' },
  { label: 'Logo right', value: 'logo_right', hint: 'Letterhead | Logo' },
  { label: 'Stacked', value: 'stacked', hint: 'Logo above letterhead' }
]

export const SIGNATURE_LAYOUT_OPTIONS: { label: string; value: SignatureLayout; hint: string }[] = [
  { label: 'Left', value: 'left', hint: 'Signature left, stamp right' },
  { label: 'Right', value: 'right', hint: 'Stamp left, signature right' },
  { label: 'Center', value: 'center', hint: 'Signature and stamp centered' }
]

function LayoutOptionButton({
  selected,
  label,
  hint,
  onClick
}: {
  selected: boolean
  label: string
  hint: string
  onClick: () => void
}) {
  return (
    <button
      className={`flex flex-1 flex-col items-start gap-0.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${
        selected ? 'border-[#2d7a4f] bg-[#f0f7f3] ring-1 ring-[#2d7a4f]' : 'border-[#ddd] bg-white hover:border-[#bbb]'
      }`}
      onClick={onClick}
      type="button"
    >
      <span className="text-sm font-medium text-[#222]">{label}</span>
      <span className="text-[11px] leading-tight text-[#777]">{hint}</span>
    </button>
  )
}

export function LetterheadLayoutSketch({ layout }: { layout: LetterheadLayout }) {
  if (layout === 'stacked') {
    return (
      <div className="flex w-full flex-col items-center gap-1.5 rounded-md border border-dashed border-[#ccc] bg-[#fafafa] px-3 py-2">
        <div className="h-5 w-10 rounded bg-[#c5d4c8]" />
        <div className="h-2 w-24 rounded bg-[#ddd]" />
        <div className="h-2 w-20 rounded bg-[#e8e8e8]" />
      </div>
    )
  }
  if (layout === 'logo_right') {
    return (
      <div className="flex w-full items-center justify-between gap-3 rounded-md border border-dashed border-[#ccc] bg-[#fafafa] px-3 py-2">
        <div className="flex flex-1 flex-col gap-1">
          <div className="h-2 w-full rounded bg-[#ddd]" />
          <div className="h-2 w-[80%] rounded bg-[#e8e8e8]" />
        </div>
        <div className="h-8 w-10 shrink-0 rounded bg-[#c5d4c8]" />
      </div>
    )
  }
  return (
    <div className="flex w-full items-center justify-between gap-3 rounded-md border border-dashed border-[#ccc] bg-[#fafafa] px-3 py-2">
      <div className="h-8 w-10 shrink-0 rounded bg-[#c5d4c8]" />
      <div className="flex flex-1 flex-col items-end gap-1">
        <div className="h-2 w-full rounded bg-[#ddd]" />
        <div className="h-2 w-[80%] rounded bg-[#e8e8e8]" />
      </div>
    </div>
  )
}

type ReportBrandingControlsProps = {
  value: FacilityReportBranding
  onChange: (next: FacilityReportBranding) => void
  /** Show letterhead layout picker (default true). */
  showLetterhead?: boolean
  /** Show signature layout picker (default true). */
  showSignature?: boolean
  /** Optional description under the section title. */
  description?: string
  /** Compact mode hides the sketch and shortens spacing. */
  compact?: boolean
  className?: string
}

const ReportBrandingControls = ({
  value,
  onChange,
  showLetterhead = true,
  showSignature = true,
  description,
  compact = false,
  className = ''
}: ReportBrandingControlsProps) => {
  const letterheadLayout = value.letterhead_layout || DEFAULT_LETTERHEAD_LAYOUT
  const signatureLayout = value.signature_layout || DEFAULT_SIGNATURE_LAYOUT

  return (
    <div className={className}>
      {description ? <p className="mb-3 text-xs text-[#777]">{description}</p> : null}

      {showLetterhead ? (
        <div className={showSignature && !compact ? 'mb-4' : undefined}>
          <p className="mb-2 text-sm font-medium text-[#333]">Letterhead layout</p>
          <div className="mb-2 flex flex-col gap-2 sm:flex-row">
            {LETTERHEAD_LAYOUT_OPTIONS.map(opt => (
              <LayoutOptionButton
                hint={opt.hint}
                key={opt.value}
                label={opt.label}
                onClick={() => onChange({ ...value, letterhead_layout: opt.value })}
                selected={letterheadLayout === opt.value}
              />
            ))}
          </div>
          {!compact ? <LetterheadLayoutSketch layout={letterheadLayout} /> : null}
        </div>
      ) : null}

      {showSignature ? (
        <div>
          <p className="mb-2 text-sm font-medium text-[#333]">Signature layout</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            {SIGNATURE_LAYOUT_OPTIONS.map(opt => (
              <LayoutOptionButton
                hint={opt.hint}
                key={opt.value}
                label={opt.label}
                onClick={() => onChange({ ...value, signature_layout: opt.value })}
                selected={signatureLayout === opt.value}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}

export default ReportBrandingControls
